import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { useSectionDrawingPreviewQuery } from "../../hooks/queries/useSectionDrawingPreviewQuery";
import type { SectionSpec } from "@/shared/models/SectionSpec";
import { formatSectionSpecRevision } from "@/shared/utils/sectionSpecRevision";
import { SECTION_SPEC_FILE_KIND } from "@/shared/models/SectionSpec";
import {
	formatSpecValue as num,
	formatWeightBand,
	SectionDetailStat,
	SectionDrawingCard,
	SectionDrawingDialog,
	sectionSpecEmptyState,
} from "../../shared/components/SectionDetailsPanelContent";

const DRAWING_KIND = {
	MILL: "mill",
} as const;

type DrawingKind = (typeof DRAWING_KIND)[keyof typeof DRAWING_KIND];

interface SectionDetailsPanelProps {
	sectionId: number | null;
	spec: SectionSpec | null;
}

export const M1SectionDetailsPanel = ({
	sectionId,
	spec,
}: SectionDetailsPanelProps) => {
	const [openedKind, setOpenedKind] = useState<DrawingKind | null>(null);
	const millPreview = useSectionDrawingPreviewQuery({
		fileId: spec?.MillDrawingUploadFileId ?? null,
		fileName: spec?.MillDrawingFileName ?? null,
	});

	const emptyState = sectionSpecEmptyState({ sectionId, spec });
	if (emptyState || !spec) return emptyState;

	const millTitle = spec.PrintNo ? `Drawing ${spec.PrintNo}` : "Mill drawing";
	const weightBand = formatWeightBand(spec);

	return (
		<Stack
			spacing={1.5}
			sx={{
				height: "100%",
				minHeight: 0,
				containerType: "inline-size",
				containerName: "m1-section-details",
			}}
		>
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				flexWrap="wrap"
				useFlexGap
				flexShrink={0}
			>
				<Typography variant="subtitle2" fontWeight={700}>
					Section details
				</Typography>
				<Chip
					size="small"
					color="primary"
					label={formatSectionSpecRevision(spec.Rev, spec.SubRev)}
				/>
			</Stack>

			<Box
				sx={{
					display: "grid",
					flexShrink: 0,
					gap: 1,
					gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
					"@container m1-section-details (min-width: 640px)": {
						gridTemplateColumns:
							"minmax(0, 1.1fr) minmax(0, 0.9fr) repeat(2, minmax(0, 1.5fr))",
					},
				}}
			>
				<SectionDetailStat label="Print no." value={spec.PrintNo ?? "-"} />
				<SectionDetailStat label="Area sq.in." value={num(spec.Area)} />
				<SectionDetailStat
					label="Weight lb/ft"
					value={num(spec.TargetLbsPerFoot)}
					hint={spec.TargetLbsPerFoot === null ? null : weightBand}
				/>
				<SectionDetailStat
					label="Description"
					value={spec.SectionDescription ?? "-"}
				/>
			</Box>

			<Box
				sx={{
					display: "grid",
					flex: 1,
					gap: 1.5,
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						lg: "minmax(0, 1fr)",
					},
					minHeight: { xs: 0, lg: 280 },
				}}
			>
				<SectionDrawingCard
					key={spec.MillDrawingUploadFileId ?? "missing-mill-drawing"}
					spec={spec}
					kind={SECTION_SPEC_FILE_KIND.MILL_DRAWING}
					fileName={spec.MillDrawingFileName}
					title="Mill drawing"
					icon={<DescriptionOutlinedIcon fontSize="small" />}
					missingMessage="Mill drawing not uploaded yet"
					preview={millPreview}
					onOpen={() => setOpenedKind(DRAWING_KIND.MILL)}
				/>
			</Box>

			{openedKind !== null && (
				<SectionDrawingDialog
					title={millTitle}
					label="Mill drawing"
					preview={millPreview}
					onClose={() => setOpenedKind(null)}
				/>
			)}
		</Stack>
	);
};
